/* Wristory storefront → CRM lead intake.
 *
 * Listens on any <form> that contains a hidden <input name="_crmKind"
 * value="BUY|SELL|SERVICE|SOURCE">. On submit:
 *   1. POSTs a typed JSON payload to the CRM intake API.
 *   2. Lets the native Shopify contact-form submission continue regardless,
 *      so the courtesy email to the store inbox is preserved.
 *
 * Markers (rendered via Liquid in each form) are NOT prefixed with
 * `contact[...]`, so they don't surface in the courtesy email.
 *
 * Configuration is injected by `snippets/wristory-crm-intake-config.liquid`
 * into a global `window.WristoryCRMIntake = { url, token }`. When either
 * value is missing the script no-ops — Shopify form behaviour is unaffected.
 *
 * Idempotency: each form gets a stable `formId` (UUID) on first interaction,
 * persisted in sessionStorage so refresh + retry don't double-create leads.
 * The CRM enforces a unique index on the same `formId` as a backstop.
 */
(function () {
  'use strict';

  // CRM intake config is optional — when missing, the CRM POST is
  // skipped per-call but the marketing opt-in path (Klaviyo) still
  // runs. This matters for stores where CRM intake isn't wired but
  // newsletter/Klaviyo is (e.g. dev stores pointed at prod Klaviyo).
  const cfg = window.WristoryCRMIntake || {};
  const HAS_CRM = !!(cfg.url && cfg.token);

  const FORM_ID_TIMEOUT_MS = 6000;

  function uuid() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    // RFC4122-ish fallback for very old browsers.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function readKind(form) {
    const el = form.querySelector('input[name="_crmKind"]');
    if (!el || !el.value) return null;
    const v = String(el.value).trim().toUpperCase();
    return v === 'BUY' || v === 'SELL' || v === 'SERVICE' || v === 'SOURCE' ? v : null;
  }

  function readProductHandle(form) {
    const el = form.querySelector('input[name="_crmProductHandle"]');
    return el ? (el.value || null) : null;
  }

  function readStockNumber(form) {
    const el = form.querySelector('input[name="_crmStockNumber"]');
    if (!el) return null;
    const v = (el.value || '').trim();
    return v || null;
  }

  function storageKeyFor(form, kind) {
    return 'wristoryCrmIntake:' + (form.id || kind || 'anon');
  }

  function getOrCreateFormId(form, kind) {
    // Per-form UUID, persisted across the same browser session so that a
    // server-side validation bounce + retry doesn't open a second lead.
    if (form.dataset.crmFormId) return form.dataset.crmFormId;
    const key = storageKeyFor(form, kind);
    let id = null;
    try { id = sessionStorage.getItem(key); } catch (_) {}
    if (!id) id = uuid();
    try { sessionStorage.setItem(key, id); } catch (_) {}
    form.dataset.crmFormId = id;
    return id;
  }

  function clearFormId(form, kind) {
    try { sessionStorage.removeItem(storageKeyFor(form, kind)); } catch (_) {}
    delete form.dataset.crmFormId;
  }

  function val(form, name) {
    const el = form.querySelector('[name="' + name + '"]');
    if (!el) return null;
    if (el.type === 'checkbox' || el.type === 'radio') return el.checked ? el.value : null;
    const v = el.value;
    return typeof v === 'string' ? v.trim() || null : null;
  }

  function vals(form, name) {
    // Multi-value (checkbox group) — returns an array of checked values.
    const out = [];
    form.querySelectorAll('[name="' + name + '"]').forEach(function (el) {
      if ((el.type === 'checkbox' || el.type === 'radio') && el.checked) {
        out.push(el.value);
      }
    });
    return out;
  }

  function valFirstChecked(form, name) {
    const el = form.querySelector('[name="' + name + '"]:checked');
    return el ? el.value : null;
  }

  function splitName(full) {
    if (!full) return { firstName: null, lastName: null };
    const parts = full.trim().split(/\s+/);
    if (parts.length === 0) return { firstName: null, lastName: null };
    if (parts.length === 1) return { firstName: parts[0], lastName: null };
    return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
  }

  function combineDateTime(date, time) {
    // Inputs come from Shopify's <input type="date"> + a fixed-options
    // <select> like "10:00 AM". Build a local-time ISO string the CRM can
    // parse with `new Date(...)`. We don't try to localize precisely —
    // operator confirms the slot manually.
    if (!date) return null;
    if (!time) return new Date(date + 'T00:00:00').toISOString();
    const m = time.match(/^(\d{1,2}):(\d{2})\s*([AP]M)?$/i);
    if (!m) return new Date(date + 'T00:00:00').toISOString();
    let hh = parseInt(m[1], 10);
    const mm = parseInt(m[2], 10);
    const ampm = (m[3] || '').toUpperCase();
    if (ampm === 'PM' && hh < 12) hh += 12;
    if (ampm === 'AM' && hh === 12) hh = 0;
    const iso = date + 'T' + String(hh).padStart(2, '0') + ':' + String(mm).padStart(2, '0') + ':00';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d.toISOString();
  }

  function buildBuyPayload(form) {
    // Drawer + PDP enquire share the BUY shape. Some fields (e.g. preferred
    // date) only exist on the viewing drawer.
    const name = splitName(val(form, 'contact[Name]'));
    return {
      contact: {
        firstName: name.firstName,
        lastName: name.lastName,
        email: val(form, 'contact[email]'),
        phone: val(form, 'contact[phone]'),
      },
      productHandle: readProductHandle(form),
      // Variant SKU = stock_number in CRM; lets the intake link this lead to
      // the specific Stock unit + Reference automatically.
      stockNumber: readStockNumber(form),
      productUrl: val(form, 'contact[Product URL]'),
      // Drawer uses contact[Watch]; PDP enquire uses contact[Interested in].
      productTitle: val(form, 'contact[Watch]') || val(form, 'contact[Interested in]'),
      preferredViewingAt: combineDateTime(
        val(form, 'contact[Preferred date]'),
        val(form, 'contact[Preferred time]'),
      ),
      additionalDetails: val(form, 'contact[body]'),
      sourcePage: window.location.pathname,
    };
  }

  function buildSellPayload(form) {
    return {
      contact: {
        firstName: val(form, 'contact[First Name]'),
        lastName: val(form, 'contact[Last Name]'),
        email: val(form, 'contact[email]'),
        phone: val(form, 'contact[Phone]'),
      },
      watch: {
        rawMake: val(form, 'contact[Brand]'),
        rawModel: val(form, 'contact[Model / Reference Number]'),
        rawReference: val(form, 'contact[Model / Reference Number]'),
        approxYear: val(form, 'contact[Approximate Year]'),
        boxPapers: valFirstChecked(form, 'contact[Box / Papers]'),
      },
      priceExpectation: val(form, 'contact[Price Expectation AUD]'),
      additionalDetails: val(form, 'contact[Additional Details]'),
      sourcePage: window.location.pathname,
    };
  }

  function buildServicePayload(form) {
    const services = vals(form, 'contact[Service Required]');
    return {
      contact: {
        firstName: val(form, 'contact[First Name]'),
        lastName: val(form, 'contact[Last Name]'),
        email: val(form, 'contact[email]'),
        phone: val(form, 'contact[Phone]'),
      },
      watch: {
        rawMake: val(form, 'contact[Brand]'),
        rawModel: val(form, 'contact[Model / Reference Number]'),
        rawReference: val(form, 'contact[Model / Reference Number]'),
        approxYear: val(form, 'contact[Approximate Year]'),
        isRunning: valFirstChecked(form, 'contact[Is the watch running?]'),
      },
      serviceRequired: services,
      serviceDetails: val(form, 'contact[Service Request Details]'),
      sourcePage: window.location.pathname,
    };
  }

  function buildSourcePayload(form) {
    // Source intake is used by two surfaces:
    //   1. The dedicated /pages/source-a-watch form — uses First/Last Name,
    //      Phone, Desired Brand, Desired Model / Reference Number, etc.
    //   2. The PDP sold-out CTA — uses the same simpler shape as the BUY
    //      enquiry form (single Name, lowercase phone, generic body),
    //      plus hidden _crmDesiredBrand / _crmDesiredModel fields populated
    //      from product metafields.
    // Fall through both shapes so either origin produces a clean payload.
    const splitFromFull = splitName(val(form, 'contact[Name]'));
    return {
      contact: {
        firstName: val(form, 'contact[First Name]') || splitFromFull.firstName,
        lastName: val(form, 'contact[Last Name]') || splitFromFull.lastName,
        email: val(form, 'contact[email]'),
        phone: val(form, 'contact[Phone]') || val(form, 'contact[phone]'),
      },
      watch: {
        rawMake:
          val(form, 'contact[Desired Brand]') ||
          val(form, '_crmDesiredBrand'),
        rawModel:
          val(form, 'contact[Desired Model / Reference Number]') ||
          val(form, '_crmDesiredModel'),
        rawReference:
          val(form, 'contact[Desired Model / Reference Number]') ||
          val(form, '_crmDesiredReference') ||
          val(form, '_crmDesiredModel'),
        boxPapers: valFirstChecked(form, 'contact[Box & Papers Requirement]'),
      },
      priceExpectation: val(form, 'contact[Target Price AUD]'),
      additionalDetails:
        val(form, 'contact[Request Details]') || val(form, 'contact[body]'),
      // PDP sold-out path includes these so the operator can see the original
      // watch the customer was interested in. Harmless on the dedicated form.
      productHandle: readProductHandle(form),
      productUrl: val(form, 'contact[Product URL]'),
      productTitle:
        val(form, 'contact[Watch]') || val(form, 'contact[Interested in]'),
      sourcePage: window.location.pathname,
    };
  }

  const BUILDERS = {
    BUY: buildBuyPayload,
    SELL: buildSellPayload,
    SERVICE: buildServicePayload,
    SOURCE: buildSourcePayload,
  };

  function readMarketingOptIn(form) {
    const box = form.querySelector('[data-wt-marketing-opt-in]');
    if (!box) return { optIn: false, source: null };
    return {
      optIn: !!box.checked,
      source: box.dataset.wtMarketingSource || null,
    };
  }

  function buildPayload(form, kind) {
    const builder = BUILDERS[kind];
    if (!builder) return null;
    const payload = builder(form);
    payload.kind = kind;
    payload.formId = getOrCreateFormId(form, kind);
    const optIn = readMarketingOptIn(form);
    payload.marketingOptIn = optIn.optIn;
    payload.marketingOptInSource = optIn.source;
    return payload;
  }

  // ─── Klaviyo Client API — subscribe-to-marketing ────────────────────
  // Same /client/subscriptions/ pattern as the wt-newsletter section.
  // Creates the profile and grants email marketing consent in one call.

  const KLAVIYO_REVISION = '2024-10-15';
  const KLAVIYO_LIST_ID = 'TGZsgk';

  // Normalise to E.164 (+CCXXXXXXXXX) for Klaviyo's phone validator.
  // Returns null if the input doesn't look like a valid phone — we then
  // omit phone_number from the payload rather than failing the whole call.
  function normalizePhoneE164(raw) {
    if (!raw) return null;
    let s = String(raw).trim();
    // Keep leading '+' if present, then strip everything that isn't a digit.
    const hasPlus = s.charAt(0) === '+';
    s = s.replace(/[^\d]/g, '');
    if (!s) return null;
    if (hasPlus) return '+' + s;
    // Australian local format: 0XXXXXXXXX → +61XXXXXXXXX
    if (s.charAt(0) === '0' && s.length >= 9 && s.length <= 11) {
      return '+61' + s.substring(1);
    }
    // Bare Australian without leading 0 or +: 61XXXXXXXXX → +61XXXXXXXXX
    if (s.substring(0, 2) === '61' && s.length >= 10 && s.length <= 12) {
      return '+' + s;
    }
    // Any other format — be conservative and skip rather than risk a 4xx.
    return null;
  }

  function klaviyoSubscribe(opts) {
    const key = window.WT_CONFIG && window.WT_CONFIG.klaviyoPublicKey;
    if (!key || !opts || !opts.email) return Promise.resolve(null);

    // NOTE: do NOT include a `subscriptions` field inside profile.attrs —
    // Klaviyo Client API revision 2024-10-15 rejects it with HTTP 400.
    // Marketing consent is granted implicitly by POSTing to /subscriptions/
    // with a list relationship (the list's opt-in setting decides whether
    // it's single or double-opt-in).
    const profileAttrs = {
      email: opts.email,
    };
    if (opts.firstName) profileAttrs.first_name = opts.firstName;
    if (opts.lastName) profileAttrs.last_name = opts.lastName;
    const phoneE164 = normalizePhoneE164(opts.phone);
    if (phoneE164) profileAttrs.phone_number = phoneE164;

    const body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: opts.customSource || 'Wristory form',
          profile: {
            data: { type: 'profile', attributes: profileAttrs },
          },
        },
        relationships: {
          list: { data: { type: 'list', id: KLAVIYO_LIST_ID } },
        },
      },
    };
    return fetch(
      'https://a.klaviyo.com/client/subscriptions/?company_id=' +
        encodeURIComponent(key),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify(body),
        mode: 'cors',
        keepalive: true,
      },
    ).catch(function () { return null; });
  }

  function postToCrm(payload) {
    // Fire and (mostly) forget. We do await it, but with a short timeout
    // so a slow CRM never blocks the courtesy-email submission. If the
    // CRM call fails the storefront still emails the inbox — operator
    // pastes the lead manually. Same fallback-shape as the legacy flow.
    const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = ctrl
      ? setTimeout(function () { ctrl.abort(); }, FORM_ID_TIMEOUT_MS)
      : null;

    return fetch(cfg.url, {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        'X-Wristory-Token': cfg.token,
      },
      body: JSON.stringify(payload),
      signal: ctrl ? ctrl.signal : undefined,
      keepalive: true,
    })
      .then(function (res) {
        return res.json().then(
          function (json) { return { ok: res.ok, json: json }; },
          function () { return { ok: res.ok, json: null }; },
        );
      })
      .catch(function (err) {
        return { ok: false, error: err };
      })
      .then(function (result) {
        if (timer) clearTimeout(timer);
        return result;
      });
  }

  function attach(form) {
    if (form.dataset.crmIntakeAttached === '1') return;
    const kind = readKind(form);
    if (!kind) return; // not a CRM-enabled form
    form.dataset.crmIntakeAttached = '1';
    // Materialise a formId at attach time so the CRM payload + a future
    // retry stay in lockstep.
    getOrCreateFormId(form, kind);

    form.addEventListener('submit', function () {
      const payload = buildPayload(form, kind);
      if (!payload) return;

      // SOURCE pipeline: stash submit context so the post-redirect
      // thank-you modal can (a) confirm it's the right pageload to
      // pop open and (b) skip the newsletter block if the user
      // already ticked the marketing opt-in box. We can't read the
      // checkbox after Shopify redirects, so persist it here.
      if (kind === 'SOURCE') {
        try {
          sessionStorage.setItem(
            'wt:sourceSubmittedAt',
            String(Date.now()),
          );
          sessionStorage.setItem(
            'wt:sourceMarketingOptIn',
            payload.marketingOptIn ? 'yes' : 'no',
          );
        } catch (_) {}
      }

      // Fire CRM POST in parallel (only when CRM intake is configured).
      // We do NOT preventDefault — Shopify's contact form continues to
      // submit and email the store inbox as it does today.
      if (HAS_CRM) {
        try {
          postToCrm(payload).then(function (result) {
            if (result && result.ok) {
              // Success — clear the persisted formId so the next genuine
              // form submission gets a fresh one.
              clearFormId(form, kind);
            }
          });
        } catch (_) {
          // Never let this throw — the user's form submission must proceed.
        }
      }

      // If the marketing opt-in checkbox is ticked, also POST to Klaviyo's
      // /client/subscriptions/ endpoint so the profile is created and given
      // email marketing consent immediately. Spam Act 2003 — express consent
      // is captured at the same instant the user submits the form. Passes
      // first_name, last_name and phone_number (E.164-normalised) so the
      // Klaviyo profile is fully populated for personalisation.
      try {
        if (payload.marketingOptIn && payload.contact && payload.contact.email) {
          klaviyoSubscribe({
            email: payload.contact.email,
            firstName: payload.contact.firstName,
            lastName: payload.contact.lastName,
            phone: payload.contact.phone,
            customSource: payload.marketingOptInSource,
          });
        }
      } catch (_) {
        // Best-effort; never block the form.
      }
    }, { capture: false });
  }

  function findForms(root) {
    const out = [];
    (root.querySelectorAll
      ? root.querySelectorAll('input[name="_crmKind"]')
      : []
    ).forEach(function (marker) {
      const f = marker.closest('form');
      if (f) out.push(f);
    });
    return out;
  }

  function init() {
    findForms(document).forEach(attach);

    // Some forms (drawer) are conditionally rendered — observe insertions.
    if (typeof MutationObserver !== 'undefined') {
      const mo = new MutationObserver(function (records) {
        records.forEach(function (rec) {
          rec.addedNodes.forEach(function (node) {
            if (node.nodeType !== 1) return;
            findForms(node).forEach(attach);
            if (node.tagName === 'INPUT' && node.name === '_crmKind') {
              const f = node.closest('form');
              if (f) attach(f);
            }
          });
        });
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
